import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ChevronUp as ChevronUpIcon, Lock as LockIcon } from "lucide-react";
import { MicOnIcon, PauseIcon, SendIcon } from "../../../assets/icon";
import { TelegramRecordingOverlay } from "../../chat/message-footer/telegram-recording-overlay/telegram-recording-overlay";
import {
  getMicButtonSx,
  getRecordingMicButtonSx,
} from "../../chat/message-footer/styles";
import {
  lockPillStyle,
  lockedSendButtonSx,
  sendTopActionStyle,
} from "../../chat/message-footer/telegram-recording-overlay/styles";
import { MicVolumeHalo } from "../../mic-volume-halo/mic-volume-halo";
import { useAsyncVoiceRecorder } from "../../../hooks/useAsyncVoiceRecorder";
import { useTelegramVoiceGesture } from "../../../hooks/useTelegramVoiceGesture";
import {
  micActionInnerStyles,
  micActionSlotStyles,
  recorderContainerStyles,
  recorderHintStyles,
  recorderRowStyles,
  uploadingOverlayStyles,
  waitHintStyles,
} from "./styles";

interface AsyncRoleplayRecorderProps {
  disabled?: boolean;
  isUploading?: boolean;
  maxSeconds?: number;
  docked?: boolean;
  onSend: (blob: Blob) => Promise<void>;
  onRecordingChange?: (recording: boolean) => void;
}

export const AsyncRoleplayRecorder: React.FC<AsyncRoleplayRecorderProps> = ({
  disabled = false,
  isUploading = false,
  maxSeconds = 120,
  docked = false,
  onSend,
  onRecordingChange,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [error, setError] = useState<string | null>(null);

  const handleComplete = useCallback(
    async (blob: Blob) => {
      setError(null);
      try {
        await onSend(blob);
      } catch {
        setError("Failed to send voice message. Please try again.");
      }
    },
    [onSend],
  );

  const {
    isRecording,
    isPaused,
    recordTimeMs,
    mediaStream,
    pausedPreviewUrl,
    formattedClockTime,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
  } = useAsyncVoiceRecorder({
    maxSeconds,
    onComplete: handleComplete,
  });

  useEffect(() => {
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  const voiceDisabled = disabled || isUploading;

  const {
    buttonRef: micButtonRef,
    phase: voiceGesturePhase,
    isGestureActive,
    isLocked: isVoiceLocked,
    dragOffsetX,
    lockProgress,
    cancelProgress,
    stopLockedRecording,
    cancelLockedRecording,
    gestureHandlers,
  } = useTelegramVoiceGesture({
    disabled: voiceDisabled,
    onStart: async () => {
      setError(null);
      try {
        await startRecording();
      } catch {
        setError("Microphone access is required to record.");
      }
    },
    onStop: stopRecording,
    onCancel: cancelRecording,
  });

  const showVoiceRecordingUi =
    (isRecording || isGestureActive) && !isUploading;

  const handleLockedSend = useCallback(() => {
    stopLockedRecording();
  }, [stopLockedRecording]);

  return (
    <Box sx={recorderContainerStyles(docked)}>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}

      <Box sx={recorderRowStyles}>
        {showVoiceRecordingUi ? (
          <TelegramRecordingOverlay
            recordTimeMs={recordTimeMs}
            clockTime={formattedClockTime}
            cancelProgress={cancelProgress}
            isLocked={isVoiceLocked}
            isPaused={isPaused}
            isCancelling={voiceGesturePhase === "cancelling"}
            dragOffsetX={dragOffsetX}
            pausedPreviewUrl={pausedPreviewUrl}
            durationSeconds={Math.max(0.1, recordTimeMs / 1000)}
            onCancelLocked={cancelLockedRecording}
          />
        ) : (
          <Typography sx={recorderHintStyles}>
            {disabled
              ? "Wait for the AI reply before recording the next message."
              : "Hold to record · slide left to cancel · slide up to lock"}
          </Typography>
        )}

        <Box
          sx={micActionSlotStyles({
            locked: isVoiceLocked,
            recordingHold: showVoiceRecordingUi && !isVoiceLocked,
          })}
        >
          <Box sx={micActionInnerStyles}>
            {showVoiceRecordingUi && !isVoiceLocked && (
              <Box sx={lockPillStyle(lockProgress, isDarkMode)} aria-hidden>
                <ChevronUpIcon size={16} strokeWidth={2.5} />
                <LockIcon size={16} strokeWidth={2.25} />
              </Box>
            )}

            {showVoiceRecordingUi && isVoiceLocked && (
              <IconButton
                type="button"
                aria-label={isPaused ? "Resume recording" : "Pause recording"}
                onClick={isPaused ? resumeRecording : pauseRecording}
                sx={sendTopActionStyle()}
              >
                {isPaused ? (
                  <MicOnIcon
                    style={{
                      width: 16,
                      height: 16,
                      color: "currentColor",
                    }}
                  />
                ) : (
                  <PauseIcon size={16} />
                )}
              </IconButton>
            )}

            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                touchAction: "none",
                position: "relative",
              }}
            >
              {showVoiceRecordingUi && !isVoiceLocked ? (
                <MicVolumeHalo
                  stream={mediaStream}
                  active={showVoiceRecordingUi && !isVoiceLocked && !isPaused}
                  buttonPx={64}
                />
              ) : null}

              <IconButton
                ref={micButtonRef}
                type="button"
                disabled={voiceDisabled && !isVoiceLocked}
                onContextMenu={(event) => {
                  event.preventDefault();
                }}
                {...(isVoiceLocked
                  ? { onClick: handleLockedSend }
                  : gestureHandlers)}
                aria-label={
                  isUploading
                    ? "Sending voice message"
                    : isVoiceLocked
                      ? "Finish recording"
                      : showVoiceRecordingUi
                        ? "Recording — release to finish, slide left to cancel, slide up to lock"
                        : "Hold to record voice message"
                }
                sx={
                  isVoiceLocked
                    ? lockedSendButtonSx(isSmallScreen)
                    : showVoiceRecordingUi
                      ? getRecordingMicButtonSx(isSmallScreen, {
                          glowing: true,
                          locked: false,
                        })
                      : getMicButtonSx(isSmallScreen)
                }
              >
                {isUploading ? (
                  <CircularProgress
                    size={18}
                    thickness={4}
                    sx={{ color: "#fff" }}
                  />
                ) : isVoiceLocked ? (
                  <SendIcon
                    width={isSmallScreen ? "20px" : "24px"}
                    height={isSmallScreen ? "20px" : "24px"}
                  />
                ) : (
                  <MicOnIcon
                    style={{
                      width: showVoiceRecordingUi
                        ? isSmallScreen
                          ? 26
                          : 30
                        : isSmallScreen
                          ? 16
                          : 18,
                      height: showVoiceRecordingUi
                        ? isSmallScreen
                          ? 26
                          : 30
                        : isSmallScreen
                          ? 16
                          : 18,
                      color: "currentColor",
                    }}
                  />
                )}
              </IconButton>
            </Box>
          </Box>
        </Box>

        {isUploading && (
          <Box sx={uploadingOverlayStyles}>
            <CircularProgress size={18} sx={{ color: "#7B49C1" }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Sending...
            </Typography>
          </Box>
        )}
      </Box>

      {disabled && !showVoiceRecordingUi && !isUploading && (
        <Typography sx={waitHintStyles}>
          Wait for the AI reply before recording the next message.
        </Typography>
      )}
    </Box>
  );
};
